import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import update from 'immutability-helper';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { apiGet } from '../brainApi';
import { getServiceEntity } from '../helper';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '35vw',
    [theme.breakpoints.down('md')]: {
      width: '70vw',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
    marginTop: '15px',
  },
}));

const NotificationPreferences = (props) => {
  const {
    notificationTypesAndPreferences,
    setNotificationTypesAndPreferences,
    employeeId,
  } = props;
  const classes = useStyles();
  const [categoriesExpanded, setCategoriesExpanded] = useState({});
  const [serviceVehicleIdToName, setServiceVehicleIdToName] = useState([]);
  const subscriptionTypesAndBooleans = [
    { type: 'subscribed_to_email', boolean: 'is_email_enabled' },
    { type: 'subscribed_to_admin', boolean: 'is_admin_enabled' },
    {
      type: 'subscribed_to_push_notification',
      boolean: 'is_push_enabled',
    },
  ];
  // Returns all the service vehicles
  const getServiceVehicles = () => {
    apiGet('/service-vehicle').then((json) => {
      json.sort((a, b) => (a.name > b.name ? 1 : -1));
      const serviceVehicleMap = {};
      json.forEach((sv) => {
        serviceVehicleMap[sv.id] = sv.name;
      });
      setServiceVehicleIdToName(serviceVehicleMap);
    });
  };
  // Returns the initial list of event types and subscription info from DB
  const getNotificationTypesAndPreferences = () => {
    const specificEmployeeRoute = employeeId ? `/${employeeId}` : '';
    apiGet(`/employee-notification-preferences${specificEmployeeRoute}`).then(
      (json) => {
        const categories = [
          ...new Set(json.map((notificationType) => notificationType.category)),
        ];
        const organizedEventTypes = {};
        categories.forEach((category) => {
          organizedEventTypes[category] = json.filter(
            (item) => item.category === category,
          );
        });
        setNotificationTypesAndPreferences(organizedEventTypes);
        setCategoriesExpanded(
          categories.reduce((o, category) => ({ ...o, [category]: false }), {}),
        );
      },
    );
  };
  // Load preferences and service vehicles from backend on load
  useEffect(() => {
    getNotificationTypesAndPreferences();
    getServiceVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPreferencesHelper = (category, eventTypeIndex, subscriptionType) => {
    const updatedPreferences = update(notificationTypesAndPreferences, {
      [category]: {
        [eventTypeIndex]: {
          subscription_info: {
            [subscriptionType]: { $apply: (value) => !value },
          },
        },
      },
    });
    setNotificationTypesAndPreferences(updatedPreferences);
  };
  // Set preferences values of entire category
  const setCategoryHelper = (category, subscriptionType, value) => {
    const updatedPreferences = update(notificationTypesAndPreferences, {
      [category]: {
        $apply: (eventTypes) =>
          eventTypes.map((eventType) => {
            const updatedEventType = eventType;
            updatedEventType.subscription_info[subscriptionType] = value;
            return updatedEventType;
          }),
      },
    });
    setNotificationTypesAndPreferences(updatedPreferences);
  };
  // Returns whether or not a subscription type is true for all entries in a category
  const isSubscriptionTypeTrueEverywhere = (category, subscription) => {
    return notificationTypesAndPreferences[category].every(
      (eventType) =>
        eventType.subscription_info[subscription.type] ||
        !eventType[subscription.boolean],
    );
  };
  // Return whether or not a subscription type has both true and false values for all event types in a category
  const isSubscriptionTypeMixed = (category, subscription) => {
    // If there is some false and some true, then subscription type is mixed for category
    return (
      !isSubscriptionTypeTrueEverywhere(category, subscription) &&
      notificationTypesAndPreferences[category].some(
        (eventType) => eventType.subscription_info[subscription.type] === true,
      )
    );
  };
  // Do any of the event types in a given category have the given delivery method enabled
  const isDeliveryMethodEnabledForAnyTypeInCategory = (
    category,
    subscriptionBoolean,
  ) => {
    return notificationTypesAndPreferences[category].some(
      (eventType) => !!eventType[subscriptionBoolean],
    );
  };
  // Toggle expanded categories
  const toggleExpandedCategory = (category) => {
    setCategoriesExpanded(
      update(categoriesExpanded, {
        [category]: { $apply: (value) => !value },
      }),
    );
  };

  // Set subscribed service vehicles
  const setSubscribedServiceVehicles = (category, eventTypeIndex, values) => {
    const updatedPreferences = update(notificationTypesAndPreferences, {
      [category]: {
        [eventTypeIndex]: {
          subscription_info: {
            subscribed_to_service_vehicle_ids: { $set: values },
          },
        },
      },
    });
    setNotificationTypesAndPreferences(updatedPreferences);
  };
  // Render the service vehicle selection dropdown
  const renderServiceVehicleSelect = (category, eventType, eventTypeIndex) => (
    <Grid item xs={2}>
      {eventType.can_limit_by_service_vehicle && (
        <>
          <InputLabel shrink>{getServiceEntity(true)}</InputLabel>
          <Select
            disabled={subscriptionTypesAndBooleans.every(
              (subscription) =>
                !notificationTypesAndPreferences[category][eventTypeIndex]
                  .subscription_info[subscription.type] ||
                !notificationTypesAndPreferences[category][eventTypeIndex][
                  subscription.boolean
                ],
            )}
            multiple
            displayEmpty
            style={{ maxWidth: '70%' }}
            onChange={(e) =>
              setSubscribedServiceVehicles(
                category,
                eventTypeIndex,
                e.target.value,
              )
            }
            value={
              eventType.subscription_info.subscribed_to_service_vehicle_ids ||
              []
            }
            renderValue={(selected) => {
              // If none are selected, all are selected
              if (selected.length === 0) return <em>All</em>;
              const serviceVehicleNames = eventType.subscription_info.subscribed_to_service_vehicle_ids.map(
                (id) => serviceVehicleIdToName[id],
              );
              return serviceVehicleNames.join(',');
            }}
          >
            {Object.entries(serviceVehicleIdToName).map(([id, name]) => (
              <MenuItem value={id}>{name}</MenuItem>
            ))}
          </Select>
        </>
      )}
    </Grid>
  );

  // Render all the components that allow viewing / changing preferences
  const renderPreferences = () => {
    const sortedNotificationTypesAndPreferences = Object.keys(
      notificationTypesAndPreferences,
    ).sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1));

    return sortedNotificationTypesAndPreferences.map((category, index) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <Grid container item key={`${category}-${index}`} direction="column">
          <Grid container alignItems="center" item spacing={1}>
            <Grid
              container
              item
              xs={3}
              direction="row"
              alignItems="center"
              onClick={() => {
                toggleExpandedCategory(category);
              }}
            >
              {categoriesExpanded[category] ? (
                <ExpandMoreIcon />
              ) : (
                <ChevronRightIcon />
              )}
              <Typography variant="subtitle2">{category}</Typography>
            </Grid>
            {subscriptionTypesAndBooleans.map((subscription) => (
              <Grid item key={`${category}-${subscription.type}`} xs>
                {isDeliveryMethodEnabledForAnyTypeInCategory(
                  category,
                  subscription.boolean,
                ) && (
                  <Checkbox
                    onClick={() =>
                      setCategoryHelper(
                        category,
                        subscription.type,
                        !isSubscriptionTypeTrueEverywhere(
                          category,
                          subscription,
                        ),
                      )
                    }
                    checked={isSubscriptionTypeTrueEverywhere(
                      category,
                      subscription,
                    )}
                    indeterminate={isSubscriptionTypeMixed(
                      category,
                      subscription,
                    )}
                  />
                )}
              </Grid>
            ))}
            {/* Filler grid item to match spacing from service vehicle select */}
            <Grid item xs={2} />
          </Grid>
          {categoriesExpanded[category] &&
            notificationTypesAndPreferences[category]
              .sort((a, b) =>
                a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1,
              )
              .map((eventType, idx) => {
                return (
                  <Grid
                    container
                    item
                    alignItems="center"
                    direction="row"
                    spacing={1}
                  >
                    <Grid item xs={3}>
                      <Tooltip
                        title={eventType.short_description}
                        placement="bottom-start"
                      >
                        <Typography
                          variant="body2"
                          style={{ marginLeft: '25%' }}
                        >
                          {eventType.name}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    {subscriptionTypesAndBooleans.map((subscription) => (
                      <Grid item xs>
                        {eventType[subscription.boolean] && (
                          <Checkbox
                            checked={
                              !!eventType.subscription_info[subscription.type]
                            }
                            onClick={() =>
                              setPreferencesHelper(
                                category,
                                idx,
                                subscription.type,
                              )
                            }
                          />
                        )}
                      </Grid>
                    ))}
                    {renderServiceVehicleSelect(category, eventType, idx)}
                  </Grid>
                );
              })}
        </Grid>
      );
    });
  };
  return (
    Object.keys(notificationTypesAndPreferences).length > 0 && (
      <Grid
        className={classes.root}
        container
        item
        direction="column"
        xs={12}
        spacing={2}
      >
        <Typography variant="h4" gutterBottom>
          Notification Preferences
        </Typography>
        <Grid item alignItems="center" container direction="row" spacing={1}>
          <Grid item xs={2} />
          <Grid item xs>
            <Typography variant="subtitle2">Email</Typography>
          </Grid>
          <Grid item xs>
            <Typography variant="subtitle2">Admin Portal</Typography>
          </Grid>
          <Grid item xs>
            <Typography variant="subtitle2">Technician App</Typography>
          </Grid>
          <Grid item xs />
        </Grid>
        {renderPreferences()}
      </Grid>
    )
  );
};
export default NotificationPreferences;
